import type { ScientificObjectRef } from '../../../scientific-objects/ScientificObjectRef';
import type { ScientificObjectSummary } from '../../../scientific-objects/ScientificObjectSummary';
import type { Transcript, TranscriptCodingRegion, TranscriptUtrSegment } from './Transcript';
import { getTranscript } from './transcriptApi';
import { RelationshipList } from '../../../knowledge/relationships/RelationshipList';
import { TranscriptCoordinateInspector } from './TranscriptCoordinateInspector';
import { useAsyncResource } from '../../../hooks/useAsyncResource';

interface Props {
  readonly reference: ScientificObjectRef;
  readonly summary: ScientificObjectSummary;
}

const BIOTYPE = { PROTEIN_CODING: 'Protein coding', NON_CODING: 'Non-coding', UNKNOWN: 'Unknown' } as const;
const STRAND = { FORWARD: 'Forward strand', REVERSE: 'Reverse strand', UNKNOWN: 'Unknown strand' } as const;
const IDS = { NCBI_REFSEQ_TRANSCRIPT: 'NCBI RefSeq Transcript', ENSEMBL_TRANSCRIPT: 'Ensembl Transcript' } as const;

export function TranscriptWorkspace({ reference, summary }: Props) {
  const state = useAsyncResource(() => getTranscript(reference.id), [reference.id], 'Unable to load this transcript.');

  return (
    <main className="transcript-workspace" data-object-id={reference.id}>
      <header className="transcript-heading">
        <p className="eyebrow">Transcript</p>
        <h1>{summary.displayName}</h1>
        <p>{reference.id}</p>
      </header>
      {state.status === 'loading' ? <p>Loading transcript…</p> : null}
      {state.status === 'error' ? <p role="alert">{state.message}</p> : null}
      {state.status === 'ready' ? <Details transcript={state.value} /> : null}
      <RelationshipList reference={reference} direction="incoming" relationshipType="HAS_TRANSCRIPT" />
      <RelationshipList reference={reference} direction="outgoing" relationshipType="TRANSLATES_TO" />
    </main>
  );
}

function Details({ transcript }: { readonly transcript: Transcript }) {
  return (
    <div className="transcript-details">
      <section>
        <h2>Overview</h2>
        <dl className="transcript-facts">
          <div>
            <dt>Preferred name</dt>
            <dd>{transcript.preferredName}</dd>
          </div>
          <div>
            <dt>Biotype</dt>
            <dd>{BIOTYPE[transcript.biotype]}</dd>
          </div>
          <div>
            <dt>Organism</dt>
            <dd>
              <i>{transcript.organism.scientificName}</i> <small>Taxonomy {transcript.organism.taxonomyId}</small>
            </dd>
          </div>
        </dl>
      </section>
      <section>
        <h2>Genomic mappings</h2>
        <div className="transcript-loci">
          {transcript.loci.map((locus, index) => (
            <article
              key={`${locus.assembly.identifier}:${locus.referenceSequence.identifier}:${locus.strand}:${index}`}
            >
              <h3>{locus.assembly.displayName}</h3>
              <dl className="transcript-facts">
                <div>
                  <dt>Assembly accession</dt>
                  <dd>{locus.assembly.identifier}</dd>
                </div>
                <div>
                  <dt>Reference sequence</dt>
                  <dd>
                    {locus.referenceSequence.displayName}
                    <small>{locus.referenceSequence.identifier}</small>
                  </dd>
                </div>
                <div>
                  <dt>Strand</dt>
                  <dd>{STRAND[locus.strand]}</dd>
                </div>
                <div>
                  <dt>Span</dt>
                  <dd>
                    [{locus.spanStart}, {locus.spanEnd})
                  </dd>
                </div>
              </dl>
              <h4>Exons</h4>
              <ol className="transcript-exons">
                {locus.exons.map((exon) => (
                  <li key={exon.ordinal}>
                    <span>Exon {exon.ordinal}</span>
                    <code>
                      [{exon.start}, {exon.end})
                    </code>
                  </li>
                ))}
              </ol>
              {locus.codingRegion ? (
                <CodingRegionDetails codingRegion={locus.codingRegion} />
              ) : (
                <p>Coding annotation unavailable.</p>
              )}
            </article>
          ))}
        </div>
        <p className="coordinate-note">Coordinates are 0-based, half-open [start, end).</p>
      </section>
      <TranscriptCoordinateInspector transcriptId={transcript.id} loci={transcript.loci} />
      <section>
        <h2>External identifiers</h2>
        {transcript.externalIdentifiers.length === 0 ? (
          <p>No external identifiers recorded.</p>
        ) : (
          <dl className="transcript-facts">
            {transcript.externalIdentifiers.map((id) => (
              <div key={`${id.namespace}:${id.value}`}>
                <dt>{IDS[id.namespace]}</dt>
                <dd>{id.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
    </div>
  );
}

function CodingRegionDetails({ codingRegion }: { readonly codingRegion: TranscriptCodingRegion }) {
  return (
    <section className="transcript-coding-region">
      <h4>Coding region</h4>
      <dl className="transcript-facts">
        <div>
          <dt>CDS length</dt>
          <dd>{codingRegion.length.toLocaleString()} nt</dd>
        </div>
        <div>
          <dt>Genomic coding span</dt>
          <dd>
            [{codingRegion.spanStart}, {codingRegion.spanEnd})
          </dd>
        </div>
      </dl>
      <h5>Segments</h5>
      <ol className="transcript-exons">
        {codingRegion.segments.map((segment) => (
          <li key={segment.ordinal}>
            <span>
              Segment {segment.ordinal} · Exon {segment.exonOrdinal}
            </span>
            <code>
              [{segment.start}, {segment.end}) · Phase {segment.phase}
            </code>
          </li>
        ))}
      </ol>
      <UtrDetails heading="5′ UTR" segments={codingRegion.fivePrimeUtr} />
      <UtrDetails heading="3′ UTR" segments={codingRegion.threePrimeUtr} />
    </section>
  );
}

function UtrDetails({
  heading,
  segments
}: {
  readonly heading: string;
  readonly segments: readonly TranscriptUtrSegment[];
}) {
  return (
    <div>
      <h5>{heading}</h5>
      {segments.length === 0 ? (
        <p>None annotated.</p>
      ) : (
        <ul className="transcript-exons">
          {segments.map((segment, index) => (
            <li key={`${segment.exonOrdinal}:${segment.start}:${index}`}>
              <span>Exon {segment.exonOrdinal}</span>
              <code>
                [{segment.start}, {segment.end})
              </code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
